import { Component, Input, OnInit } from "@angular/core";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
} from "@angular/forms";
import {
    NgbActiveModal,
    NgbInputDatepicker,
    NgbDateParserFormatter,
    NgbDate,
} from "@ng-bootstrap/ng-bootstrap";
import { ContractDetails } from "src/app/core/models/contract-details";
import { TypedFormGroup } from "src/app/shared-utils/typed-forms.helper";
import { NgbDateCustomParserFormatter } from "src/app/core/Util/ngb-date-parser-customer-formatter";
import { ContractDetailsApiService } from "src/app/core/api/contract-details-api.service";
import { convertToDate } from "src/app/core/Util/util";
import { CommonModule } from "@angular/common";
import { ToastrService } from "ngx-toastr";
import { QuillEditorComponent, QuillModules } from "ngx-quill";

@Component({
    selector: "app-create-contract-details-modal",
    standalone: true,
    imports: [
        TranslatePipe,
        FormsModule,
        ReactiveFormsModule,
        NgbInputDatepicker,
        CommonModule,
        QuillEditorComponent,
    ],
    providers: [
        {
            provide: NgbDateParserFormatter,
            useClass: NgbDateCustomParserFormatter,
        },
    ],
    templateUrl: "./create-contract-details-modal.component.html",
    styleUrl: "./create-contract-details-modal.component.scss",
})
export class CreateContractDetailsModalComponent implements OnInit {
    @Input() public contractId?: number;
    @Input() public extensionId?: number;

    public modules: QuillModules = {
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['clean'],
        ],
    };

    public ContractDetailsForm: TypedFormGroup<Partial<ContractDetails>>;

    constructor(
        private formBuilder: FormBuilder,
        private activeModal: NgbActiveModal,
        private contractDetailsApiService: ContractDetailsApiService,
        private translateService: TranslateService,
        private toastrService: ToastrService
    ) {}

    private buildForm() {
        this.ContractDetailsForm = this.formBuilder.group({
            id: [null],
            po: [""],
            caseNumber: [""],
            budget: [""],
            startDate: [null],
            endDate: [null],
            comment: [""],
        }) as TypedFormGroup<Partial<ContractDetails>>;
    }

    ngOnInit() {
        this.buildForm();
    }

    onSave() {
        if (!this.contractId && !this.extensionId) {
            return;
        }

        const formValue = this.ContractDetailsForm.value;

        // No field is mandatory, but block creating an entry with no data at all.
        const hasAnyData =
            !!formValue.po ||
            !!formValue.caseNumber ||
            !!formValue.budget ||
            !!formValue.comment ||
            !!formValue.startDate ||
            !!formValue.endDate;
        if (!hasAnyData) {
            this.translateService
                .get("contractDetails.form.emptyForm")
                .subscribe((res: string) => this.toastrService.error(res));
            return;
        }

        const postData: ContractDetails = {
            id: formValue.id ?? 0,
            contractId: this.contractId,
            extensionId: this.extensionId,
            po: formValue.po,
            caseNumber: formValue.caseNumber,
            budget: formValue.budget,
            comment: formValue.comment,
            endDate: formValue.endDate
                ? convertToDate(formValue.endDate as unknown as NgbDate)
                : undefined,
            startDate: formValue.startDate
                ? convertToDate(formValue.startDate as unknown as NgbDate)
                : undefined,
        };
        this.contractDetailsApiService.create(postData).subscribe({
            next: (response) => {
                this.translateService.get('contractDetails.form.created').subscribe((res: string) => {
                    this.toastrService.success(res);
                });
                this.activeModal.close(response);
            },
            error: () => {
                this.translateService.get('contractDetails.form.createdError').subscribe((res: string) => {
                    this.toastrService.error(res);
                });
            }
        });
    }

    onClose() {
        this.activeModal.dismiss();
    }
}
